import React, { useEffect, useState } from 'react';
import { MessageCircle, Music2, MessagesSquare, LineChart } from 'lucide-react';
import { fetchPublicSettings } from '../../features/settings/api/settings';
import type { SiteSettings } from '../../types';

const waLink = (num: string) => {
  const digits = (num || '').replace(/[^0-9]/g, '');
  return digits ? `https://wa.me/${digits}` : '';
};

export function PublicFooter() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    fetchPublicSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  const wa = waLink(settings?.support_whatsapp || '');
  const tiktok = settings?.social_tiktok_url || '';
  const discord = settings?.social_discord_url || '';

  const socials = [
    { key: 'whatsapp', url: wa, label: 'WhatsApp', Icon: MessageCircle, cls: 'hover:text-emerald-400 hover:border-emerald-400/40' },
    { key: 'tiktok', url: tiktok, label: 'TikTok', Icon: Music2, cls: 'hover:text-pink-400 hover:border-pink-400/40' },
    { key: 'discord', url: discord, label: 'Discord', Icon: MessagesSquare, cls: 'hover:text-indigo-400 hover:border-indigo-400/40' },
  ].filter((s) => s.url);

  return (
    <footer data-testid="public-footer" className="border-t border-white/10 bg-[#050914] mt-16">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center overflow-hidden shrink-0">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <LineChart className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            )}
          </div>
          <div className="leading-tight">
            <p className="text-white font-medium tracking-tight">{settings?.site_name || 'Cuantrader'}</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80">Community</p>
          </div>
        </div>

        <div className="flex flex-col items-center sm:items-end gap-3">
          {socials.length > 0 && (
            <div className="flex items-center gap-3" data-testid="public-social-links">
              <span className="text-xs uppercase tracking-wider text-white/40 mr-1">Gabung Komunitas</span>
              {socials.map(({ key, url, label, Icon, cls }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`public-social-${key}`}
                  className={`w-10 h-10 rounded-xl border border-white/10 bg-white/[0.02] text-white/60 flex items-center justify-center transition-colors ${cls}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </a>
              ))}
            </div>
          )}
          <p className="text-xs text-white/30">© {new Date().getFullYear()} {settings?.site_name || 'Cuantrader Community'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
